import _ from "lodash";
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

// components
import PromptList from "./components/PromptList";
import Sidebar from "./components/Sidebar";
import AITutorLibrary from "../AITutorLibrary";
import AITutorLibraryV2 from "../AITutorLibraryV2";

// design system
import {
  Button,
  Modal,
  Tooltip,
  SearchInput,
  EmptyState,
} from "@toddle-edu/ds-web";
import { OpenBookStarOutlined } from "@toddle-edu/ds-icons";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";

// modules
import {
  getCurrentCurriculumProgramType,
  getUserId,
} from "modules/CommonSelectors";

// third party
import { connect } from "react-redux";

// utils
import { getPromptsForSuggestionLibrary } from "../../modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { scrollIntoView, isElementInViewPortVertical } from "Utils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";
import { getSettingValue } from "Utils/ConfigurationUtils";

// scss
import classes from "./SuggestionLibrary.scss";

const styles = {
  bodyStyle: {
    padding: "0",
    overflow: "hidden",
  },
  modalHeaderStyle: {
    padding: "16px 24px",
  },
  modalFooterStyle: {
    padding: "16px 32px",
  },
  subtitleStyle: {
    maxWidth: "375px",
  },
};

const SuggestionLibrary = React.memo(props => {
  const {
    t,
    userId,
    onPromptSuggestionModalClose,
    initialGroupType,
    onPromptSelection,
    isAIResponseLoading,
    displayPrompts,
    flattenedPrompts,
    searchText,
    setSearchText,
    isInitialState,
    setCurrentDialogueAction,
  } = props;

  const [selectedPrompt, setSelectedPrompt] = useState({});
  const [selectedSidebarItem, setSelectedSidebarItem] = useState(null);
  const [isScrollEventAllowed, setIsScrollEventAllowed] = useState(false);

  const sidebarScrollRef = useRef(null);

  // from chat if user clicks "More" then scroll to that category in sidebar
  useLayoutEffect(() => {
    if (initialGroupType) {
      if (_.some(displayPrompts, { id: initialGroupType })) {
        setSelectedSidebarItem(initialGroupType);
      } else {
        const id = _.find(displayPrompts, ({ children }) =>
          _.includes(children, initialGroupType)
        )?.id;

        if (id) setSelectedSidebarItem(id);
      }
      setTimeout(() => scrollIntoPromptCategory(initialGroupType));
    } else {
      setSelectedSidebarItem(_.find(displayPrompts, { depth: 2 })?.id);
      setIsScrollEventAllowed(true);
    }
  }, [initialGroupType]);

  // whenever user search something then select the first category which came according to search result
  useEffect(() => {
    if (_.isNil(searchText)) return;
    setSelectedSidebarItem(_.find(displayPrompts, { depth: 2 })?.id);
  }, [searchText]);

  // as user is scrolling, scroll to the updated selected sidebar item
  useEffect(() => {
    if (sidebarScrollRef?.current) {
      const element = sidebarScrollRef?.current;

      if (element) {
        const { top } = sidebarScrollRef.current?.getBoundingClientRect() || {};

        if (!isElementInViewPortVertical({ element, bottomOffset: -top })) {
          element?.scrollIntoView({ block: "center" });
        }
      }
    }
  }, [selectedSidebarItem]);

  // on sidebar item click, scroll to the selected prompt category
  const scrollIntoPromptCategory = id => {
    const element = document.getElementById(`display-prompt-${id}`);
    if (element) {
      scrollIntoView({ element, scrollAfter: 10 });
      setIsScrollEventAllowed(false);
    }
  };

  const onChangeSelectedSidebarItem = id => {
    scrollIntoPromptCategory(id);
    setSelectedSidebarItem(id);
  };

  const onGenerateClick = () => {
    const showResetAIChatDialog = getSettingValue({
      name: "ShowResetAIChatDialog",
      readFromUserAndOrgLevel: true,
    });

    if (isInitialState) {
      onPromptSelection(selectedPrompt);
      onPromptSuggestionModalClose();
    } else {
      if (!showResetAIChatDialog) {
        setCurrentDialogueAction({
          action: "CLEAR_CHAT_AND_GENERATE",
          params: {
            selectedPrompt,
          },
        });
      } else {
        onPromptSelection(selectedPrompt);
        onPromptSuggestionModalClose({ action: "CLEAR_CHAT" });
      }
    }
  };

  const showEmptyState = _.every(flattenedPrompts, ({ depth }) => depth !== 1);

  return (
    <Modal width="1180px" height="720px" onClose={onPromptSuggestionModalClose}>
      <Modal.Header hasSeparator={true} style={styles.modalHeaderStyle}>
        <div className={classes.headerContainer}>
          <div className={classes.headerLeft}>
            <div className={classes.iconWrapper}>
              <OpenBookStarOutlined variant="link" />
            </div>
            {t("common:suggestion_library")}
          </div>
          <div className={classes.headerRight}>
            <SearchInput
              value={searchText || ""}
              onChange={setSearchText}
              placeholder={t("ai:le_suggestion_library_search_placeholder")}
              focusSearchBySlash
            />
          </div>
        </div>
      </Modal.Header>

      <Modal.Body style={styles.bodyStyle}>
        <div className={classes.bodyContainer}>
          {showEmptyState ? (
            <EmptyState
              illustration={NoSearchResultsIllustration}
              title={t("common:noResultHeading")}
              subtitle={t("ai:le_suggestion_library_no_result_found_msg")}
              subtitleStyle={styles.subtitleStyle}
            />
          ) : (
            <>
              <Sidebar
                sidebarScrollRef={sidebarScrollRef}
                displayPrompts={displayPrompts}
                selectedSidebarItem={selectedSidebarItem}
                setSelectedSidebarItem={onChangeSelectedSidebarItem}
              />
              <PromptList
                t={t}
                searchText={searchText}
                prompts={flattenedPrompts}
                selectedPrompt={selectedPrompt}
                setSelectedPrompt={setSelectedPrompt}
                selectedSidebarItem={selectedSidebarItem}
                setSelectedSidebarItem={setSelectedSidebarItem}
                isScrollEventAllowed={isScrollEventAllowed}
                setIsScrollEventAllowed={setIsScrollEventAllowed}
              />
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer hasSeparator={true} style={styles.modalFooterStyle}>
        <div className={classes.footerContainer}>
          <Button
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="medium"
            onClick={onPromptSuggestionModalClose}
          >
            {t("common:cancel")}
          </Button>

          <ConditionalWrapper
            type={"wrapperAsElement"}
            condition={isAIResponseLoading}
            wrapper={
              <Tooltip
                placement={"left"}
                tooltip={t("classRoom:prompt_generate_button_disable_text")}
                showArrow={true}
              />
            }
          >
            <Button
              dsVersion="2.0"
              variant="progressive"
              size="medium"
              disabled={_.isEmpty(selectedPrompt) || isAIResponseLoading}
              onClick={onGenerateClick}
            >
              {t("common:select")}
            </Button>
          </ConditionalWrapper>
        </div>
      </Modal.Footer>
    </Modal>
  );
});

const SuggestionLibraryWrapper = props => {
  const [searchText, setSearchText] = useState(null);

  if (props.isAITutor) {
    if (getIsAITutorModalitiesEnabled()) {
      return <AITutorLibraryV2 {...props} />;
    }
    return <AITutorLibrary {...props} />;
  } else {
    const { flattenedPrompts } = getPromptsForSuggestionLibrary({
      ...props,
      searchText,
    });

    const displayPrompts = _.filter(flattenedPrompts, {
      promptType: "display",
    });

    return (
      <SuggestionLibrary
        {...props}
        searchText={searchText}
        setSearchText={setSearchText}
        displayPrompts={displayPrompts}
        flattenedPrompts={flattenedPrompts}
      />
    );
  }
};

SuggestionLibraryWrapper.displayName = "SuggestionLibraryWrapper";
SuggestionLibrary.displayName = "SuggestionLibrary";

const mapStateToProps = state => {
  const userId = getUserId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);

  return {
    userId,
    curriculumProgramType,
  };
};

export default connect(mapStateToProps, null)(SuggestionLibraryWrapper);
